import { ReactNode, createContext, useContext, useCallback, useEffect, useRef, Ref } from 'react'
import { useIdentity } from '@vp/ubik-context'
import { APP_TENANT, TEST_USER_ID } from '../constants/app'
import TrackingJS, { useTracking } from '@vp/react-tracking'
import {
  ButtonClickedProps,
  DownloadPageLogoDetailsProps,
  EmailLogoDataGeneratedProps,
  FlyOutClickedPros,
  FlyOutClosedEventProps,
  FlyOutViewedEventProps,
  getButtonClickedEvent,
  getDownloadPageLogoDetailsEvent,
  getEmailLogoDataGeneratedEvent,
  getExperimentClickedEvent,
  getFlyOutClickedEvent,
  getFlyOutClosedEvent,
  getFlyOutViewedEvent,
  getLogoDownloadedEvent,
  getLogomakerCrosssellComponentEvent,
  getNavigationClickedEvent,
  getPageEvent,
  getProductViewedEvent,
  getPromotionClickedEvent,
  getPromotionViewedEvent,
  getTabSelectedEvent,
  getTrackLogoVariantsAdded,
  LogoDownloadedProps,
  LogomakerCrosssellComponentProps,
  LogoVariantsAddedProps,
  NavigationClickedProps,
  PageContext,
  PageViewedProps,
  ProductViewedProps,
  PromotionProps,
  TabSelectedProps,
} from '../utilities/events'
import ChannelTracking from '@vp/channel-tracking'
import { Tracking } from '@vp/tracking'
import { isClient } from '../utilities/isClient'
import { BrandShopExperiments, BrandShopExperimentVariations } from '../components/BrandShopPage/BrandShopExperiments'

interface EventTracking {
  trackPage: (props?: PageViewedProps) => void
  trackDownloadPageLogoDetails: (props: DownloadPageLogoDetailsProps) => void
  trackTabSelected: (props: TabSelectedProps) => void
  trackLogoVariantsAdded: (props: LogoVariantsAddedProps) => void
  trackNavigationClicked: (props: NavigationClickedProps) => void
  trackButtonClicked: (props: ButtonClickedProps) => void
  trackLogomakerCrosssellComponent: (props: LogomakerCrosssellComponentProps) => void
  trackFlyOutViewed: (props: FlyOutViewedEventProps) => void
  trackFlyOutClosed: (props: FlyOutClosedEventProps) => void
  trackFlyOutClicked: (props: FlyOutClickedPros) => void
  trackProductViewed: (props: ProductViewedProps) => void
  trackLogoDownloaded: (props: LogoDownloadedProps) => void
  trackEmailLogoDataGenerated: (props: EmailLogoDataGeneratedProps) => void
  trackPromotionViewed: (props: PromotionProps) => void
  trackPromotionClicked: (props: PromotionProps) => void
  useTrackOnComponentView: (trackingFunction: any, props?: any) => any
  trackExperimentClicked: (
    props: PromotionProps,
    experimentName: BrandShopExperiments,
    variationName: BrandShopExperimentVariations,
  ) => void
}

export const EventTrackingContext = createContext<EventTracking | null>(null)

export const useEventTracking = (): EventTracking => {
  const context = useContext(EventTrackingContext)
  if (context === null) {
    throw new Error('useEventTracking must be used with EventTrackingContextProvider')
  }
  return context
}

interface TrackingContextProviderProps {
  children: ReactNode
  pageName: string
  locale?: string
}

export const EventTrackingContextProvider = ({ children, pageName, locale }: TrackingContextProviderProps) => {
  const auth = useIdentity()
  //@ts-ignore
  const canonicalId = auth?.identity ? auth.identity.canonicalId : ''
  const { tracking: segmentTracking } = useTracking()

  const pageContext: PageContext = isClient()
    ? {
        pageUrl: window.location.href,
        locale: locale || 'en-us',
        source: new URLSearchParams(window.location.search).get('source') || '',
        utmId: ChannelTracking.getTrackingData().utm_id || '',
        utmSource: ChannelTracking.getTrackingData().utm_source || '',
        pageName: pageName,
      }
    : {
        pageUrl: '',
        locale: locale,
        source: '',
        utmId: '',
        utmSource: '',
        pageName: pageName,
      }

  const trackingFuncRef = useRef<Tracking>(inMemoryTracking)

  useEffect(() => {
    trackingFuncRef.current = getTrackingFunction(canonicalId, segmentTracking as Tracking)
  }, [canonicalId, segmentTracking])

  const trackPage = useCallback((props: PageViewedProps = {}) => {
    const { pageName, payload } = getPageEvent(props, pageContext)
    trackingFuncRef.current.page(pageName, payload)
  }, [])

  const trackDownloadPageLogoDetails = useCallback((props: DownloadPageLogoDetailsProps) => {
    const { eventName, payload } = getDownloadPageLogoDetailsEvent(props, pageContext)
    trackingFuncRef.current.track(eventName, payload)
  }, [])

  const trackTabSelected = useCallback((props: TabSelectedProps) => {
    const { eventName, payload } = getTabSelectedEvent(props, pageContext)
    trackingFuncRef.current.track(eventName, payload)
  }, [])

  const trackLogoVariantsAdded = useCallback((props: LogoVariantsAddedProps) => {
    const { eventName, payload } = getTrackLogoVariantsAdded(props, pageContext)
    trackingFuncRef.current.track(eventName, payload)
  }, [])

  const trackNavigationClicked = useCallback((props: NavigationClickedProps) => {
    const { eventName, payload } = getNavigationClickedEvent(props, pageContext)
    trackingFuncRef.current.track(eventName, payload)
  }, [])

  const trackPromotionClicked = useCallback((props: PromotionProps) => {
    const { eventName, payload } = getPromotionClickedEvent(props, pageContext)
    trackingFuncRef.current.track(eventName, payload)
  }, [])
  const trackPromotionViewed = useCallback((props: PromotionProps) => {
    const { eventName, payload } = getPromotionViewedEvent(props, pageContext)
    trackingFuncRef.current.track(eventName, payload)
  }, [])
  const trackExperimentClicked = useCallback(
    (props: PromotionProps, experimentName: BrandShopExperiments, variationName: BrandShopExperimentVariations) => {
      const { eventName, payload } = getExperimentClickedEvent(props, pageContext)
      trackingFuncRef.current.track(eventName, { ...payload, experimentName: experimentName, variationName: variationName })
    },
    [],
  )

  function useTrackOnComponentView(trackingFunction: any, props?: any) {
    if (props === undefined || props === null) {
      return
    }
    const ref = useRef(null)
    const wasTracked = useRef(false) // Prevent duplicate tracking
    useEffect(() => {
      const element = ref.current
      if (!element) {
        return
      }
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !wasTracked.current) {
            wasTracked.current = true
            trackingFunction(props)
            observer.disconnect()
          } else {
            wasTracked.current = false
          }
        },
        { threshold: 0.5 },
      )
      observer.observe(element)
      return () => {
        if (element) {
          observer.disconnect()
        }
      }
    }, [props])

    return ref
  }

  const trackButtonClicked = useCallback((props: ButtonClickedProps) => {
    const { eventName, payload } = getButtonClickedEvent(props, pageContext)
    trackingFuncRef.current.track(eventName, payload)
  }, [])

  const trackLogomakerCrosssellComponent = useCallback((props: LogomakerCrosssellComponentProps) => {
    const { eventName, payload } = getLogomakerCrosssellComponentEvent(props, pageContext)
    trackingFuncRef.current.track(eventName, payload)
  }, [])

  const trackFlyOutViewed = useCallback(
    (props: FlyOutViewedEventProps) => {
      const { eventName, payload } = getFlyOutViewedEvent(props, pageContext)
      trackingFuncRef.current.track(eventName, payload)
    },
    [pageContext],
  )

  const trackFlyOutClosed = useCallback(
    (props: FlyOutClosedEventProps) => {
      const { eventName, payload } = getFlyOutClosedEvent(props, pageContext)
      trackingFuncRef.current.track(eventName, payload)
    },
    [pageContext],
  )

  const trackFlyOutClicked = useCallback(
    (props: FlyOutClickedPros) => {
      const { eventName, payload } = getFlyOutClickedEvent(props, pageContext)
      trackingFuncRef.current.track(eventName, payload)
    },
    [pageContext],
  )

  const trackProductViewed = useCallback((props: ProductViewedProps) => {
    const { eventName, payload } = getProductViewedEvent(props, pageContext)
    trackingFuncRef.current.track(eventName, payload)
  }, [])

  const trackLogoDownloaded = useCallback((props: LogoDownloadedProps) => {
    const { eventName, payload } = getLogoDownloadedEvent(props, pageContext)
    trackingFuncRef.current.track(eventName, payload)
  }, [])
  const trackEmailLogoDataGenerated = useCallback((props: EmailLogoDataGeneratedProps) => {
    const { eventName, payload } = getEmailLogoDataGeneratedEvent(props, pageContext)
    trackingFuncRef.current.track(eventName, payload)
  }, [])
  const env = process.env.UBIK_ENV || 'prod'

  return (
    <>
      <TrackingJS
        tenant={APP_TENANT}
        environment={env}
        integrations={{ All: true }}
        culture={locale || 'en-us'}
        suppressReloadOnConsentSubmit={false}
      />
      <EventTrackingContext.Provider
        value={{
          trackPage,
          trackEmailLogoDataGenerated,
          trackFlyOutViewed,
          trackFlyOutClosed,
          trackFlyOutClicked,
          trackDownloadPageLogoDetails,
          trackLogomakerCrosssellComponent,
          trackTabSelected,
          trackProductViewed,
          trackLogoVariantsAdded,
          trackNavigationClicked,
          trackButtonClicked,
          trackLogoDownloaded,
          trackPromotionViewed,
          trackPromotionClicked,
          useTrackOnComponentView,
          trackExperimentClicked,
        }}
      >
        {children}
      </EventTrackingContext.Provider>
    </>
  )
}

export const getTrackingFunction = (canonicalId: string, segmentTracking: Tracking): Tracking => {
  // disable tracking for new relic synthetic monitor user
  const trackingDisabled = TEST_USER_ID === canonicalId || canonicalId === ''
  return trackingDisabled ? inMemoryTracking : segmentTracking
}

const inMemoryTracking: Tracking = {
  visit: () => null,
  page: (pageName: string) => {
    console.debug('Tracking disabled, skipping tracking for page: ', pageName)
  },
  track: (eventName: string) => {
    console.debug('Tracking disabled, skipping event: ', eventName)
  },
  trackLink: () => {},
  trackNavigation: () => {},
  identify: () => {},
  reset: () => {},
  setAnonymousId: () => {},
  version: () => 'tracking-disabled',
}
